// scanShared — shared vocabulary, axis helpers, raw-data panel, and
// chrome primitives for the scan pipeline surfaces. Extracted verbatim
// from ScanPipeline.jsx (pure move).

import { useState } from "react";
import {
  findIngredient, findHub,
  resolveStorageFromHubChain, getCanonicalHome, inferHubFromInfo,
} from "../../../data/ingredients";
import { FRIDGE_TILES } from "../../../lib/fridgeTiles";
import { PANTRY_TILES } from "../../../lib/pantryTiles";
import { FREEZER_TILES } from "../../../lib/freezerTiles";
import { FONT, COLOR, CHIP_TONES } from "../../../lib/tokens";

// Axes shown as chips in the review strip. Ordered as a breadcrumb
// the user reads left-to-right: where it lives → which shelf →
// which hub family → the specific canonical → brand/state/cut.
//
// "category" was DROPPED — it duplicated the tile in every UI we
// showed (DAIRY/DAIRY redundant rows). Tile already carries the
// categorical signal that matters.
//
// "hub" is a synthetic axis derived from the canonical's parentId
// at render time. Lets the breadcrumb read FRIDGE → DAIRY →
// CHEESE → GJETOST instead of skipping the family layer.
export const AXES = [
  { key: "location",    label: "Location",  tone: CHIP_TONES.location,  editor: "location" },
  { key: "tileId",      label: "Tile",      tone: CHIP_TONES.location,  editor: "tile" },
  { key: "hub",         label: "Hub",       tone: CHIP_TONES.canonical, synthetic: true },
  { key: "canonicalId", label: "Canonical", tone: CHIP_TONES.canonical, displayKey: "canonicalName", editor: "canonical" },
  { key: "brand",       label: "Brand",     tone: CHIP_TONES.canonical, editor: "brand" },
  // state axis retired from surfacing per
  // feedback_state_axis_inferred.md — inferred from canonical, not
  // surfaced as a chip. Field still exists on the row for legacy
  // compat (canonicalAutofillSync still writes it); UI just doesn't
  // render it on review / missing surfaces.
  { key: "cut",         label: "Cut",       tone: CHIP_TONES.cut,       editor: "cut" },
];

// Hubs that have anatomical cuts. Cut chip only renders for
// canonicals in these hubs — everything else (cheese, dairy,
// pantry, produce) skips the cut axis entirely.
export const HUBS_WITH_CUTS = new Set([
  "chicken_hub", "beef_hub", "pork_hub", "turkey_hub",
]);

export const FOUND_SOURCES = new Set([
  "user", "correction", "off", "usda", "infer", "autofill",
  "canonical-info", "tag-hints", "name-infer",
  "upc-correction", "canonical-brand-correction",
  "kitchen-home",
]);

// Common unit shortlist for the size picker. The full unit registry
// has dozens; surfacing them all on a chip-grid would be noise.
export const SIZE_UNIT_OPTIONS = ["oz", "lb", "g", "kg", "ml", "fl oz", "L", "ct", "each", "package"];

// Common shelf categories — short editable list. Matches the same
// values that tagHintsToAxes emits so a user pick is round-trippable
// with the auto-classifier.
export const CATEGORY_OPTIONS = [
  { id: "produce",    label: "Produce" },
  { id: "dairy",      label: "Dairy" },
  { id: "meat",       label: "Meat" },
  { id: "seafood",    label: "Seafood" },
  { id: "grain",      label: "Grain / Pasta / Rice" },
  { id: "pantry",     label: "Pantry / Dry" },
  { id: "frozen",     label: "Frozen" },
  { id: "beverage",   label: "Beverage" },
  { id: "spice",      label: "Spice" },
  { id: "condiment",  label: "Condiment / Sauce" },
  { id: "baking",     label: "Baking" },
  { id: "snack",      label: "Snack" },
];

// Synthetic-axis support — "hub" derives from the bound canonical's
// parentId at render time. Lookup goes through findIngredient +
// findHub so the user sees a clean hub label ("Cheese", "Beef")
// instead of the raw parentId slug. Synthetic canonicals (DB-only)
// carry their hub on info.hub instead of parentId — read both.
//
// Bundled canonicals seeded via seed() without a parentId
// (gochugaru, paprika, oats, and a long tail of older entries) fall
// through to inferHubFromInfo, which maps category/subtype/slug to a
// registered hub. Same code path resolveStorageFromHubChain uses for
// the storage cascade — keep them in sync.
export function hubIdForCanonical(canonicalId, dbInfo) {
  if (!canonicalId) return null;
  const ing = findIngredient(canonicalId);
  if (ing?.parentId) return ing.parentId;
  if (dbInfo?.hub && typeof dbInfo.hub === "string") return dbInfo.hub;
  if (ing) {
    const inferred = inferHubFromInfo(ing, ing.id);
    if (inferred) return inferred.endsWith("_hub") ? inferred : `${inferred}_hub`;
  }
  return null;
}

export function hubLabelForCanonical(canonicalId, dbInfo) {
  const hubId = hubIdForCanonical(canonicalId, dbInfo);
  if (!hubId) return null;
  const hub = findHub(hubId);
  return hub?.name || null;
}

// Tile id derived from canonical's hub-chain storage. Used to fill
// the Tile axis from canonical alone when scan-time autofill didn't
// land a tileId on the draft. Reads location from the draft (or
// canonical's home) so the right per-location tile is chosen.
export function tileIdForCanonical(canonicalId, dbInfo, location) {
  if (!canonicalId) return null;
  const ing = findIngredient(canonicalId);
  if (!ing) return null;
  // getCanonicalHome takes the canonicalId STRING. Passing the
  // ingredient object made findIngredient(object) return null and
  // the entire 4-tier cascade collapsed to null — gochugaru's tile
  // resolved via the resolveStorageFromHubChain fallback below
  // INSTEAD of through the full cascade, so admin/curated tiles
  // never made it to the breadcrumb.
  const home = getCanonicalHome(canonicalId, dbInfo) || resolveStorageFromHubChain(ing) || null;
  const tile = home?.tile;
  if (!tile) return null;
  if (typeof tile === "string") return tile;
  if (typeof tile === "object") {
    const loc = location || home?.location || null;
    if (loc && tile[loc]) return tile[loc];
    // No known location — return the first concrete tile id we find
    // so the breadcrumb still has SOMETHING to show.
    const first = Object.values(tile).find(Boolean);
    return first || null;
  }
  return null;
}

export function tileLabelForCanonical(canonicalId, dbInfo, location) {
  const tileId = tileIdForCanonical(canonicalId, dbInfo, location);
  if (!tileId) return null;
  const all = [...PANTRY_TILES, ...FRIDGE_TILES, ...FREEZER_TILES];
  const t = all.find((x) => x.id === tileId);
  return t?.label || String(tileId).replace(/_/g, " ");
}

export function isFound(draft, axis, dbInfo) {
  if (axis.synthetic && axis.key === "hub") {
    return !!hubLabelForCanonical(draft.canonicalId, dbInfo);
  }
  // Tile is "found" either because the draft has a stored tileId
  // from a real source, OR because the canonical's hub chain
  // resolves to one — both are concrete enough to render. Without
  // the chain branch, every fresh scan with no draft.tileId showed
  // "Missing: tile" even when the canonical's parent hub already
  // pinned the location/tile (gochugaru → spice_hub → spices_dried_herbs).
  if (axis.key === "tileId") {
    const val = draft.tileId;
    const src = draft.sources?.tileId;
    if (val && src && FOUND_SOURCES.has(src)) return true;
    return !!tileIdForCanonical(draft.canonicalId, dbInfo, draft.location);
  }
  // Location: same as tile — derivable from canonical's home if the
  // user hasn't picked one yet.
  if (axis.key === "location") {
    const val = draft.location;
    const src = draft.sources?.location;
    if (val && src && FOUND_SOURCES.has(src)) return true;
    if (!draft.canonicalId) return false;
    const home = getCanonicalHome(draft.canonicalId, dbInfo)
      || resolveStorageFromHubChain(draft.canonicalId);
    return !!home?.location;
  }
  const val = draft[axis.key];
  if (val == null || val === "") return false;
  const src = draft.sources?.[axis.key];
  return !!src && FOUND_SOURCES.has(src);
}

export function axisDisplay(draft, axis, dbInfo) {
  if (axis.synthetic && axis.key === "hub") {
    return hubLabelForCanonical(draft.canonicalId, dbInfo) || "";
  }
  if (axis.key === "tileId") {
    if (draft.tileId) return String(draft.tileId).replace(/_/g, " ");
    return tileLabelForCanonical(draft.canonicalId, dbInfo, draft.location) || "";
  }
  if (axis.key === "location") {
    if (draft.location) return String(draft.location);
    if (!draft.canonicalId) return "";
    const home = getCanonicalHome(draft.canonicalId, dbInfo)
      || resolveStorageFromHubChain(draft.canonicalId);
    return home?.location || "";
  }
  if (axis.displayKey && draft[axis.displayKey]) return draft[axis.displayKey];
  return draft[axis.key] || "";
}

// Build the placement breadcrumb for the scan review. Order matches
// kitchen-card's IdentityBreadcrumb but flattened to a single string:
//   Pantry · Spices & Dried Herbs · Spice · Gochugaru
// Walks the hub chain so a deep canonical reads all its tiers.
export function placementBreadcrumb(draft, dbInfo) {
  if (!draft?.canonicalId) return null;
  const ing = findIngredient(draft.canonicalId);

  const home = getCanonicalHome(draft.canonicalId, dbInfo)
    || (ing ? resolveStorageFromHubChain(ing) : null)
    || null;
  const location = draft.location || home?.location || null;
  const tileId = draft.tileId || tileIdForCanonical(draft.canonicalId, dbInfo, location);
  const allTiles = [...PANTRY_TILES, ...FRIDGE_TILES, ...FREEZER_TILES];
  const tileLabel = tileId
    ? (allTiles.find((t) => t.id === tileId)?.label || String(tileId).replace(/_/g, " "))
    : null;

  // Walk the hub chain — outermost (top-level family) → innermost
  // (canonical's direct hub). Cap at 5 tiers as a safety; chains rarely
  // exceed 2-3.
  const hubChain = [];
  let current = hubIdForCanonical(draft.canonicalId, dbInfo);
  for (let i = 0; i < 5 && current; i += 1) {
    const hub = findHub(current);
    if (hub) {
      hubChain.unshift(hub.name || String(current).replace(/_hub$/, "").replace(/_/g, " "));
      current = hub.parentHub || null;
    } else {
      hubChain.unshift(String(current).replace(/_hub$/, "").replace(/_/g, " "));
      current = null;
    }
  }

  const canonicalLabel = draft.canonicalName || ing?.name || draft.canonicalId;

  const slots = [];
  if (location) slots.push(String(location).charAt(0).toUpperCase() + String(location).slice(1));
  if (tileLabel) slots.push(tileLabel);
  for (const h of hubChain) slots.push(h);
  if (canonicalLabel) slots.push(canonicalLabel);
  // Drop adjacent duplicates (e.g. when the tile name and the top hub
  // name are the same word) so the line stays clean.
  const deduped = slots.filter((v, i) => i === 0 || v.toLowerCase() !== slots[i - 1].toLowerCase());
  return deduped.length > 0 ? deduped.join(" · ") : null;
}

export function formatUpc(raw) {
  if (!raw) return "";
  const d = String(raw).replace(/\D/g, "");
  if (d.length === 12) return `${d[0]} ${d.slice(1, 6)} ${d.slice(6, 11)} ${d[11]}`;
  if (d.length === 13) return `${d[0]} ${d.slice(1, 7)} ${d.slice(7)}`;
  if (d.length === 8)  return `${d.slice(0, 4)} ${d.slice(4)}`;
  return d;
}

export function confidenceColor(c) {
  if (c === "high")   return COLOR.leaf;   // teal — high confidence
  if (c === "low")    return COLOR.rose;   // burnt — flag for review
  // Medium / default: was mustard (COLOR.gold) — unreadable yellow
  // on cream background. Use ink instead for clear contrast.
  return COLOR.ink;
}

// ── Raw scan panel — full data visibility ───────────────────────────
// Default-collapsed in review (so the chips read first), default-open
// in propose (so the user knows what Haiku had to work with).

// Split a correction row into (USDA-provenance fields, admin/human
// fields) so the diagnostic panel can show "USDA gave us this brand,
// the household locked this canonical" instead of one mushy block.
export function partitionByProvenance(correction) {
  const prov = correction?.sourceProvenance || {};
  const fields = ["canonicalId", "brand", "name", "tileId", "location", "state", "cut", "packageSizeAmount", "packageSizeUnit"];
  const provKey = (clientField) => ({
    canonicalId: "canonical_id", brand: "brand", name: "name",
    tileId: "tile_id", location: "location", state: "state", cut: "cut",
    packageSizeAmount: "package_size_amount", packageSizeUnit: "package_size_unit",
  })[clientField];
  const usda  = {};
  const admin = {};
  const other = {};
  for (const f of fields) {
    const v = correction?.[f];
    if (v == null || v === "") continue;
    const tag = prov[provKey(f)];
    if      (tag === "usda")  usda[f]  = v;
    else if (tag === "admin") admin[f] = v;
    else                       other[f] = v;
  }
  return { usda, admin, other };
}

// Tag explaining how a given axis ended up with its value. Reads
// straight from the store's sources map so this stays honest as
// new resolver paths get added.
export const SOURCE_LABELS = {
  user:                       "you typed",
  correction:                 "household memory",
  off:                        "Open Food Facts",
  usda:                       "USDA",
  "ai-verified":              "Haiku",
  infer:                      "name match",
  "name-infer":               "name match",
  autofill:                   "canonical defaults",
  "canonical-info":           "canonical defaults",
  "tag-hints":                "OFF category tags",
  "upc-correction":           "household memory (UPC)",
  "canonical-brand-correction": "household memory (canonical+brand)",
  "scan-created-pending":     "synthetic mint",
  "ai-recipe-add":            "recipe seed",
  "kitchen-home":             "your kitchen",
};

export function RawScanPanel({ rawScan, draft, startOpen = false }) {
  const [open, setOpen] = useState(!!startOpen);
  if (!rawScan) return null;
  const { off, correction, popularPkg, merged, error } = rawScan;

  // Placement debug — what does the canonical's hub chain say vs.
  // what's actually on the draft right now. Surfaces the
  // canonicalAutofillSync output so we can see when the resolver
  // produced the right tile but a later tier blew it away, vs. when
  // the resolver itself returned null.
  const placementDebug = (() => {
    if (!draft?.canonicalId) return null;
    let chainTile = null;
    let chainLoc  = null;
    let chainErr  = null;
    try {
      const ing  = findIngredient(draft.canonicalId);
      const home = getCanonicalHome(draft.canonicalId, null)
        || (ing ? resolveStorageFromHubChain(ing) : null);
      chainLoc = home?.location || null;
      const tile = home?.tile;
      if (typeof tile === "string") chainTile = tile;
      else if (tile && typeof tile === "object") {
        const loc = chainLoc || "pantry";
        chainTile = tile[loc] || Object.values(tile).find(Boolean) || null;
      }
    } catch (e) {
      chainErr = e?.message || String(e);
    }
    const hubId = hubIdForCanonical(draft.canonicalId, null);
    return {
      draftTile:     draft.tileId || "(null)",
      draftLoc:      draft.location || "(null)",
      tileSource:    draft.sources?.tileId || "(none)",
      locSource:     draft.sources?.location || "(none)",
      chainTile:     chainTile || "(null)",
      chainLoc:      chainLoc || "(null)",
      hubId:         hubId || "(null)",
      chainErr,
    };
  })();
  const { usda, admin, other } = partitionByProvenance(correction);
  const hasUsda  = Object.keys(usda).length  > 0;
  const hasAdmin = Object.keys(admin).length > 0;
  const hasOther = Object.keys(other).length > 0;
  const tierLabel =
    correction?.source === "global" ? "GLOBAL"
    : correction?.source === "family" ? "FAMILY"
    : null;

  // Resolution breadcrumb — every FOUND axis with its source so the
  // user can read the card from the top and answer "how did the
  // system arrive at this?" without opening a debugger. Reads from
  // draft.sources, the same map ScanPipeline already maintains.
  const resolution = (() => {
    const sources = draft?.sources || {};
    const axes = [
      { key: "canonicalId", label: "Canonical", value: draft?.canonicalName || draft?.canonicalId },
      { key: "brand",       label: "Brand",     value: draft?.brand },
      { key: "tileId",      label: "Tile",      value: draft?.tileId },
      { key: "location",    label: "Location",  value: draft?.location },
      { key: "state",       label: "State",     value: draft?.state },
      { key: "cut",         label: "Cut",       value: draft?.cut },
      { key: "packageAmount", label: "Size",
        value: draft?.packageAmount && draft?.packageUnit ? `${draft.packageAmount} ${draft.packageUnit}` : null },
    ];
    return axes
      .filter((a) => a.value != null && a.value !== "")
      .map((a) => ({ ...a, src: sources[a.key] || sources[a.key === "packageAmount" ? "packageUnit" : a.key] || null }));
  })();

  return (
    <div style={rawPanelShellStyle}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mcm-focusable"
        style={rawPanelHeaderStyle}
      >
        <span style={{ fontFamily: FONT.forma, fontSize: 10, fontWeight: 700, color: COLOR.muted, letterSpacing: "0.18em", textTransform: "uppercase" }}>
          What we found · {open ? "hide" : "show"}
        </span>
        <span style={{ color: COLOR.muted, fontSize: 11, fontFamily: FONT.forma }}>{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div style={{ padding: "0 12px 12px", display: "flex", flexDirection: "column", gap: 12 }}>
          {error && <RawRow label="Error" value={error} tone={COLOR.rose} />}

          {/* Resolution breadcrumb. Every value the form is showing,
              tagged with the layer that put it there. This is the
              first thing to read on the panel — it answers "how did
              we get here?" before the per-source dumps. */}
          {resolution.length > 0 && (
            <RawSource title="Resolution · how each value got here">
              {resolution.map((a) => (
                <RawRow
                  key={a.key}
                  label={a.label}
                  value={`${a.value} · ${SOURCE_LABELS[a.src] || a.src || "default"}`}
                />
              ))}
            </RawSource>
          )}

          {/* Placement debug — temporary diagnostic. Shows the
              canonical's hub-chain resolution side-by-side with the
              draft's current tileId/location so we can see when the
              chain produces the right tile but the draft holds the
              wrong one (a later override blew it away). */}
          {placementDebug && (
            <RawSource title="Placement debug · canonical chain vs draft">
              <RawRow label="Draft tileId"    value={placementDebug.draftTile} />
              <RawRow label="Draft location"  value={placementDebug.draftLoc} />
              <RawRow label="Tile source"     value={placementDebug.tileSource} />
              <RawRow label="Loc source"      value={placementDebug.locSource} />
              <RawRow label="Chain tileId"    value={placementDebug.chainTile} />
              <RawRow label="Chain location"  value={placementDebug.chainLoc} />
              <RawRow label="Chain hub id"    value={placementDebug.hubId} />
              {placementDebug.chainErr && (
                <RawRow label="Chain error"   value={placementDebug.chainErr} tone={COLOR.rose} />
              )}
            </RawSource>
          )}

          <RawSource title="Open Food Facts (OFF)" empty={!off}>
            {off && (
              <>
                <RawRow label="Product name" value={off.productName} />
                <RawRow label="Generic name" value={off.genericName} />
                <RawRow label="Brand"        value={off.brand} />
                <RawRow label="Quantity"     value={off.quantity} />
                <RawRow label="Categories"   value={Array.isArray(off.categoryHints) ? off.categoryHints.join(", ") : null} />
                <RawRow label="Ingredients"  value={off.ingredientsText} truncate />
              </>
            )}
          </RawSource>

          {/* USDA fields are extracted from the correction row via
              source_provenance — when the ingest pipeline filled
              brand/name/size from USDA Branded Foods, the column is
              tagged "usda". Surfacing them as a distinct source means
              "Haiku said it, USDA said it, household said it" no
              longer collapse into one anonymous block. */}
          <RawSource title="USDA (via barcode_identity_corrections)" empty={!hasUsda}>
            {hasUsda && (
              <>
                {usda.canonicalId && <RawRow label="Canonical" value={usda.canonicalId} />}
                {usda.brand       && <RawRow label="Brand"     value={usda.brand} />}
                {usda.name        && <RawRow label="Name"      value={usda.name} />}
                {usda.packageSizeAmount && <RawRow label="Size" value={`${usda.packageSizeAmount} ${usda.packageSizeUnit || ""}`.trim()} />}
              </>
            )}
          </RawSource>

          {/* Admin-locked fields ride the global tier. These are the
              fields a human admin promoted via the dead-end queue or
              the legacy admin panel. Tagged "admin" in
              source_provenance — they win over external re-ingests. */}
          <RawSource title="Admin-locked (global)" empty={!hasAdmin}>
            {hasAdmin && (
              <>
                {admin.canonicalId && <RawRow label="Canonical" value={admin.canonicalId} />}
                {admin.brand       && <RawRow label="Brand"     value={admin.brand} />}
                {admin.name        && <RawRow label="Name"      value={admin.name} />}
                {admin.tileId      && <RawRow label="Tile"      value={admin.tileId} />}
                {admin.location    && <RawRow label="Location"  value={admin.location} />}
                {admin.state       && <RawRow label="State"     value={admin.state} />}
                {admin.cut         && <RawRow label="Cut"       value={admin.cut} />}
              </>
            )}
          </RawSource>

          {/* Household memory — the correction tier label tells you
              global vs family at a glance, instead of forcing you to
              parse source_provenance to figure out which table the
              row came from. */}
          <RawSource
            title={tierLabel ? `Household memory · ${tierLabel} tier` : "Household memory"}
            empty={!correction || (!hasOther && !hasUsda && !hasAdmin)}
          >
            {correction && hasOther && (
              <>
                {other.canonicalId && <RawRow label="Canonical" value={other.canonicalId} />}
                {other.brand       && <RawRow label="Brand"     value={other.brand} />}
                {other.name        && <RawRow label="Name"      value={other.name} />}
                {other.tileId      && <RawRow label="Tile"      value={other.tileId} />}
                {other.location    && <RawRow label="Location"  value={other.location} />}
                {other.state       && <RawRow label="State"     value={other.state} />}
                {other.cut         && <RawRow label="Cut"       value={other.cut} />}
                {other.packageSizeAmount && <RawRow label="Size" value={`${other.packageSizeAmount} ${other.packageSizeUnit || ""}`.trim()} />}
                {correction.correctionCount > 1 && (
                  <RawRow label="Vote count" value={String(correction.correctionCount)} />
                )}
              </>
            )}
          </RawSource>

          {popularPkg && (
            <RawSource title="Popular size (other households)">
              <RawRow label="Amount" value={`${popularPkg.amount} ${popularPkg.unit}`} />
              {popularPkg.count && <RawRow label="Votes" value={String(popularPkg.count)} />}
            </RawSource>
          )}
          {merged && (
            <RawSource title="Merged (this is what fills the form)">
              <RawRow label="Brand" value={merged.brand} />
              <RawRow label="Product name" value={merged.productName} />
              <RawRow label="Size" value={merged.packageSizeAmount && merged.packageSizeUnit ? `${merged.packageSizeAmount} ${merged.packageSizeUnit}` : null} />
              <RawRow label="Hints" value={Array.isArray(merged.categoryHints) ? merged.categoryHints.join(", ") : null} />
            </RawSource>
          )}
        </div>
      )}
    </div>
  );
}

export function RawSource({ title, empty, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{
        fontFamily: FONT.forma, fontSize: 9, fontWeight: 700,
        color: COLOR.dim, letterSpacing: "0.16em", textTransform: "uppercase",
      }}>
        {title}{empty && " · nothing"}
      </div>
      {children}
    </div>
  );
}

export function RawRow({ label, value, tone = COLOR.ink, truncate = false }) {
  if (value == null || value === "") return null;
  const v = String(value);
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
      <div style={{
        flex: "0 0 90px",
        fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
        color: COLOR.dim, letterSpacing: "0.10em", textTransform: "uppercase",
      }}>
        {label}
      </div>
      <div style={{
        flex: 1, minWidth: 0,
        fontFamily: FONT.forma, fontSize: 12, fontWeight: 500,
        color: tone, lineHeight: 1.4,
        ...(truncate ? { display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" } : {}),
      }}>
        {v}
      </div>
    </div>
  );
}

// Category-keyed fallback sizes — surfaced when usePopularPackages
// has no observations + the canonical hasn't been AI-enriched yet.
// Every canonical falls into one of these buckets so the picker is
// NEVER empty; the user can always tap a chip instead of typing.
export const FALLBACK_SIZES_BY_CATEGORY = {
  spice:    [["0.5","oz"],["1","oz"],["1.7","oz"],["2","oz"],["2.5","oz"],["4","oz"]],
  baking:   [["1","lb"],["2","lb"],["4","lb"],["5","lb"],["10","oz"],["16","oz"]],
  dairy:    [["8","oz"],["16","oz"],["32","oz"],["1","gal"],["0.5","gal"],["6","oz"]],
  meat:     [["1","lb"],["2","lb"],["3","lb"],["8","oz"],["16","oz"]],
  seafood:  [["6","oz"],["8","oz"],["12","oz"],["1","lb"]],
  produce:  [["1","count"],["3","count"],["1","lb"],["2","lb"],["5","lb"]],
  grain:    [["1","lb"],["2","lb"],["16","oz"],["32","oz"]],
  beverage: [["12","fl_oz"],["16","fl_oz"],["20","fl_oz"],["1","l"],["2","l"]],
  pantry:   [["6","oz"],["8","oz"],["12","oz"],["16","oz"],["32","oz"]],
  frozen:   [["8","oz"],["12","oz"],["16","oz"],["32","oz"]],
  condiment:[["8","oz"],["12","oz"],["16","oz"],["32","oz"]],
};
export const FALLBACK_DEFAULT = [["1","oz"],["2","oz"],["8","oz"],["16","oz"],["1","lb"]];

export function fallbackSizesFor(canonicalId) {
  const ing = canonicalId ? findIngredient(canonicalId) : null;
  const cat = ing?.category || null;
  const list = (cat && FALLBACK_SIZES_BY_CATEGORY[cat]) || FALLBACK_DEFAULT;
  return list.map(([amount, unit]) => ({
    amount: Number(amount),
    unit,
    brand: null,
    n: 0,
    source: "fallback",
  }));
}

export function mergeSuggestedAndFallback(observed, fallback, limit = 8) {
  const seen = new Set();
  const out = [];
  const push = (entry) => {
    if (out.length >= limit) return;
    const sig = `${entry.amount}|${entry.unit}`;
    if (seen.has(sig)) return;
    seen.add(sig);
    out.push(entry);
  };
  for (const r of (observed || [])) push(r);
  for (const r of (fallback || [])) push(r);
  return out;
}

// ── Shared chrome ───────────────────────────────────────────────────

export function Header({ upc, kicker, title }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={kickerStyle}>{kicker}</div>
      <div style={{
        fontFamily: FONT.detail, fontStyle: "italic", fontWeight: 500,
        fontSize: 22, lineHeight: 1.2, color: COLOR.ink,
      }}>
        {title}
      </div>
      {upc && (
        <div style={{ fontFamily: FONT.forma, fontSize: 10, fontWeight: 500, color: COLOR.muted, letterSpacing: "0.10em" }}>
          UPC {upc}
        </div>
      )}
    </div>
  );
}

export function Bucket({ label, hint, tone, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontFamily: FONT.forma, fontSize: 10, fontWeight: 700, color: tone, letterSpacing: "0.18em", textTransform: "uppercase" }}>
          {label}
        </span>
        <span style={{ fontFamily: FONT.detail, fontStyle: "italic", fontSize: 12, color: COLOR.muted }}>
          {hint}
        </span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{children}</div>
    </div>
  );
}

// FOUND chips: GREEN. All FOUND chips share one tone — the per-axis
// tone (canonical=tan, location=blue, etc.) was producing a confetti
// of colors that made the chip row hard to read. Single green
// signals "this axis is set, tap to edit" uniformly.
export const FOUND_GREEN = "#2F8F83";    // COLOR.leaf
export const FOUND_GREEN_BG = "rgba(47,143,131,0.10)";
export const FOUND_GREEN_BORDER = "rgba(47,143,131,0.45)";

// MISSING chips: RED. Same uniform-color logic — one red, not a
// shape-shifting tone per axis.
export const MISSING_RED = "#D96B2B";    // COLOR.rose
export const MISSING_RED_BORDER = "rgba(217,107,43,0.55)";

export function FoundChip({ tone, label, value, onClick }) {
  return (
    <button
      type="button" onClick={onClick} className="mcm-focusable"
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 10px",
        background: FOUND_GREEN_BG, border: `1px solid ${FOUND_GREEN_BORDER}`,
        borderRadius: 8,
        fontFamily: FONT.forma, fontSize: 11, fontWeight: 600,
        color: FOUND_GREEN, cursor: "pointer", touchAction: "manipulation",
      }}
    >
      <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.14em", opacity: 0.7, textTransform: "uppercase" }}>
        {label}
      </span>
      <span style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {String(value)}
      </span>
    </button>
  );
}

export function MissingChip({ label, onClick }) {
  return (
    <button
      type="button" onClick={onClick} className="mcm-focusable"
      style={{
        display: "inline-flex", alignItems: "center",
        padding: "6px 10px",
        background: "transparent", border: `1px dashed ${MISSING_RED_BORDER}`,
        borderRadius: 8,
        fontFamily: FONT.forma, fontSize: 11, fontWeight: 600,
        color: MISSING_RED, cursor: "pointer", touchAction: "manipulation",
      }}
    >
      + {String(label).toLowerCase()}
    </button>
  );
}

export function Spinner({ size = 22 }) {
  return (
    <div style={{
      width: size, height: size,
      border: `2px solid ${COLOR.border}`,
      borderTopColor: COLOR.ink,
      borderRadius: size / 2,
      animation: "scan-spin 0.9s linear infinite",
    }}>
      <style>{`@keyframes scan-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export const kickerStyle = {
  fontFamily: FONT.forma, fontSize: 10, fontWeight: 700,
  color: COLOR.muted, letterSpacing: "0.20em", textTransform: "uppercase",
};

export function primaryActionStyle(enabled) {
  return {
    width: "100%", padding: "14px",
    background: enabled ? COLOR.ink : "transparent",
    color:      enabled ? "#FFFFFF" : COLOR.sub,
    border: `1px solid ${enabled ? COLOR.ink : COLOR.border}`,
    borderRadius: 12,
    fontFamily: FONT.forma, fontSize: 12, fontWeight: 700,
    letterSpacing: "0.14em", textTransform: "uppercase",
    cursor: enabled ? "pointer" : "not-allowed",
    touchAction: "manipulation",
  };
}

export const secondaryActionStyle = {
  flex: 1, padding: "12px",
  background: "transparent",
  color: COLOR.ink,
  border: `1px solid ${COLOR.border}`,
  borderRadius: 12,
  fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
  letterSpacing: "0.10em",
  cursor: "pointer", touchAction: "manipulation",
};

export const ghostActionStyle = {
  padding: "10px",
  background: "transparent", color: COLOR.muted,
  border: "none",
  fontFamily: FONT.forma, fontSize: 11, fontWeight: 500,
  letterSpacing: "0.06em",
  cursor: "pointer", touchAction: "manipulation",
};

export const rawPanelShellStyle = {
  background: COLOR.deep,
  border: `1px solid ${COLOR.edge}`,
  borderRadius: 12,
};

export const rawPanelHeaderStyle = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  width: "100%",
  padding: "10px 12px",
  background: "transparent", border: "none",
  cursor: "pointer", touchAction: "manipulation",
};
